/*
 * Running work, from anywhere in the app: a quiet button beside the bell while something runs (a spinner, a count and
 * what it is: "2 updates running", or "3 tasks running" for a mix; just the count on a phone), and a panel with each
 * task's steps, a progress bar, the time so far and its log. Finished work stays under "Done recently" for ten
 * minutes, with what came of it when the API says. Starting something toasts "Started · See progress", so people can
 * move on. It reads the org's operations (api/tasks.ts), the same data the in-page progress views read, so they
 * agree, and it survives navigation and reloads. The person's own work in other orgs (api/work.ts) is in it too, its
 * rows naming the org ("Whitehardt · Acme · Live") and linking into it, so what they started stays in the header
 * wherever they go, and counts with the rest.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Popover as P } from "radix-ui";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { keys } from "../api/keys";
import { inTray, isActiveItem, itemsWords, runCounts, runFinished, runOperationIds, runTitle, takeAnnouncement, titleCount, useBulkRuns, type BulkItem, type BulkRun, type RunAction } from "../api/bulk";
import { onWatchStart, watchedHomeOf } from "../api/live";
import { isCopyFirst, isPhpFirst, progressive } from "../api/chains";
import { useRefreshOnUpdateWork } from "../api/orgUpdates";
import { useOperations, useServers, useSites } from "../api/queries";
import { itemSteps, opIndex, progressOf, runningWords, START_TOAST_KINDS, tasksFrom, useTypical, type OpIndex, type Task, type TaskStep, type Typical } from "../api/tasks";
import type { OperationWithLogs } from "../api/types";
import { cx } from "../lib/cx";
import { formatDuration, plural, relativeTime } from "../lib/format";
import { useNow } from "../lib/hooks";
import { StatusPill, type PillIcon, type Tone } from "../ui/Badge";
import { ToneIcon } from "../ui/EnvChip";
import { IconAlertCircle, IconCheck, IconCheckCircle, IconChevronRight, Spinner } from "../ui/icons";
import { useToast } from "../ui/Toast";
import { useOrg, useOrgPath } from "./org";
import { useOrgMoves, type Move } from "../api/moves";
import { MoveTaskRow, trayMoves } from "../pages/moves/MoveTask";
import { useWorkElsewhere, type WorkOrg } from "../api/work";
import { fromLines, UpdateVersions } from "./updateVersions";

// The toast's "See progress" opens the panel from wherever the toast is.
const openListeners = new Set<() => void>();
export const openTray = () => openListeners.forEach((l) => l());

/** Another org's work (the person's own, api/work.ts): its org. Null for the open org's. */
const orgOf = (x: object): WorkOrg | null => ("org" in x ? (x.org as WorkOrg) : null);

/** The org's tasks, running and recent, and the person's own in other orgs, with what they're about in words. */
function useTasks() {
  const org = useOrg();
  const ops = useOperations({ limit: 100 });
  const servers = useServers();
  const sites = useSites();
  const here = useBulkRuns();
  const away = useWorkElsewhere();
  // The Updates page and the sidebar's count follow the same operations: read again as update work moves on.
  useRefreshOnUpdateWork();
  // One list of each, this org's first; work elsewhere once (an agency's run here, its items' work in a client's org).
  const allOps = useMemo(() => {
    const ids = new Set((ops.data ?? []).map((o) => o.id));
    return [...(ops.data ?? []), ...away.operations.filter((o) => !ids.has(o.id))];
  }, [ops.data, away.operations]);
  const runs = useMemo(() => {
    const ids = new Set(here.map((b) => b.id));
    return [...here, ...away.runs.filter((b) => !ids.has(b.id))];
  }, [here, away.runs]);
  // A bulk run's work is in its own grouped task, each item with its steps, so none of it is listed again on its own:
  // the restore point and the update after it alike (an item follows one and then the other).
  const inRuns = useMemo(() => runOperationIds(runs, allOps), [runs, allOps]);
  const idx = useMemo(() => opIndex(allOps), [allOps]);
  const live = allOps.filter((o) => o.status === "queued" || o.status === "sent" || o.status === "running");
  const typical = useTypical([...new Map(live.map((o) => [o.serverId, { orgId: orgOf(o)?.id ?? org.id, serverId: o.serverId }])).values()]);
  const ticking = live.length > 0 || runs.some((b) => !runFinished(b));
  const now = useNow(true, ticking ? 1_000 : 30_000);
  // Every operation is read, the runs' too, so a restore point always finds the work after it.
  const { running, recent } = useMemo(() => tasksFrom(allOps, typical, now, inRuns), [allOps, typical, now, inRuns]);
  // Server moves: one task each, whatever ops they're made of.
  const moveList = useOrgMoves();
  const moves = useMemo(() => trayMoves([...(moveList.data ?? []), ...away.moves.filter((m) => !moveList.data?.some((x) => x.id === m.id))], now), [moveList.data, away.moves, now]);
  const siteName = (m: Move) => ("siteName" in m ? (m.siteName as string | null) : (sites.data?.find((s) => s.id === m.siteId)?.name ?? null));
  // "Acme · Live", or the server's name for server work. Another org's leads with the org and leaves the server to its
  // own pages, as that org's tray may not name it: "Whitehardt · Acme · Live", "Hosterr · test-1".
  const where = (t: Task) => {
    if (t.org) {
      const p = t.place;
      return [t.org.name, ...(p?.site ? [p.site, p.environment] : [p?.server])].filter(Boolean).join(" · ");
    }
    const site = t.siteId ? sites.data?.find((s) => s.id === t.siteId) : undefined;
    const env = site && t.environmentId ? (["dev", "test", "live"] as const).find((n) => site.pipeline?.[n]?.environmentId === t.environmentId) : undefined;
    const server = servers.data?.find((s) => s.id === t.serverId)?.name;
    if (site) return [site.name, env ? env[0]!.toUpperCase() + env.slice(1) : null, server ? `on ${server}` : null].filter(Boolean).join(" · ");
    return server ?? null;
  };
  return { running, recent, runs: runs.filter((b) => inTray(b, now)), idx, typical, moves, siteName, where, now, ready: ops.isSuccess };
}

/** Steps as one line, "Restore point · Update WP Mail SMTP", with the current one marked. Only for tasks of two or more. */
export function StepLine({ steps }: { steps: TaskStep[] }) {
  if (steps.length < 2) return null;
  return (
    <ol className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12.5px]" aria-label="Steps">
      {steps.map((s, i) => {
        const current = s.state === "running" || s.state === "starting";
        return (
          <li key={i} className="flex items-center gap-1.5">
            {i ? <span className="text-faint">·</span> : null}
            <span className={cx("inline-flex items-center gap-1", current ? "font-medium text-fg" : s.state === "done" ? "text-dim" : s.state === "failed" ? "text-bad" : "text-faint")} aria-current={current ? "step" : undefined}>
              {s.state === "done" ? <IconCheck size={12} className="text-good" aria-label="Done:" /> : current ? <Spinner size={11} className="text-info" /> : s.state === "failed" ? <IconAlertCircle size={12} aria-label="Failed:" /> : null}
              {s.label}
              {s.state === "skipped" ? <span className="sr-only"> (didn't run)</span> : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * One bar for the whole task, a segment per step: done segments full, the running one as far as its usual time says,
 * or a band sliding along it when that isn't known (and while it starts).
 */
export function TaskBar({ t, now }: { t: Pick<Task, "title" | "steps">; now: number }) {
  const p = progressOf(t, now);
  const pct = Math.round((p.done + (p.current ?? 0)) * 100);
  return (
    <div
      role="progressbar"
      aria-label={`${t.title}, progress`}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={p.current === null ? undefined : pct}
      aria-valuetext={p.current === null ? `Step ${t.steps.findIndex((s) => s.state !== "done") + 1} of ${t.steps.length}, how long it takes isn't known` : `${pct}%`}
      className="mt-2 flex h-1.5 gap-0.5 overflow-hidden rounded-full"
    >
      {t.steps.map((s, i) => {
        const w = p.weights[i]! * 100;
        const running = s.state === "running" || s.state === "starting";
        const inner = s.state === "done" ? 100 : running && p.current !== null ? Math.min(100, (p.current / p.weights[i]!) * 100) : 0;
        return (
          <span key={i} className="relative h-full overflow-hidden bg-meter-track first:rounded-l-full last:rounded-r-full" style={{ width: `${w}%` }}>
            {s.state === "starting" || (running && p.current === null) ? (
              <span className="hx-indeterminate absolute inset-0" />
            ) : (
              <span className={cx("absolute inset-y-0 left-0 transition-[width] duration-700", s.state === "failed" ? "bg-bad-fill" : s.state === "done" ? "bg-good-fill" : "bg-info-fill")} style={{ width: `${inner}%` }} />
            )}
          </span>
        );
      })}
    </div>
  );
}

/**
 * How a run's items read, by what the run does: "2 updating", "1 updated", "1 didn't update" in its count, and
 * "Updating · 14s", "Updated", "Didn't update" on each item.
 */
type RunWords = { running: string; done: string; failed: string };
const UPDATE_RUN: RunWords = { running: "updating", done: "updated", failed: "didn't update" };
const RUN_WORDS: Partial<Record<RunAction, RunWords>> = {
  "updates.selected": UPDATE_RUN,
  "updates.install": UPDATE_RUN,
  "backup.create": { running: "backing up", done: "backed up", failed: "failed" },
  "health.check": { running: "checking", done: "checked", failed: "failed" },
  "cache.purge": { running: "clearing", done: "cleared", failed: "failed" },
};
const OTHER_RUN: RunWords = { running: "running", done: "done", failed: "failed" };
const cap = (w: string) => w.charAt(0).toUpperCase() + w.slice(1);

/* A step's chip: done in green, running in blue with a spinner, failed in red, the rest gray. */
const STEP_TONE: Record<TaskStep["state"], { tone: Tone; icon?: PillIcon }> = {
  waiting: { tone: "neutral", icon: "pending" },
  starting: { tone: "progress" },
  running: { tone: "progress" },
  done: { tone: "good", icon: "check" },
  failed: { tone: "bad" },
  skipped: { tone: "neutral", icon: "dash" },
};

const currentStep = (steps: TaskStep[] | null) => steps?.findIndex((s) => s.state === "running" || s.state === "starting") ?? -1;

/**
 * Where an item stands, in words for its chip: "Waiting", "Starting", "Taking a restore point · 8s", "Updating · 14s",
 * "Updated" (or "Updated 1 of 2" when some of its updates didn't install), "Didn't update", "Skipped".
 */
function itemState(i: BulkItem, steps: TaskStep[] | null, w: RunWords, updated: string, now: number): { tone: Tone; icon?: PillIcon; words: string } {
  switch (i.status) {
    case "skipped":
      return { tone: "neutral", icon: "dash", words: "Skipped" };
    case "queued":
    case "will_run":
      return { tone: "neutral", icon: "clock", words: "Waiting" };
    case "succeeded":
      return { tone: "good", words: w === UPDATE_RUN ? updated : cap(w.done) };
    case "failed":
      return { tone: "bad", words: cap(w.failed) };
  }
  const k = currentStep(steps);
  const s = k >= 0 ? steps![k]! : null;
  if (!s || s.state === "starting") return { tone: "progress", words: k > 0 && w === UPDATE_RUN ? "Starting the update" : "Starting" };
  const doing = k === 0 && steps!.length > 1 ? (s.op?.kind === "backup.create" ? "Taking a restore point" : progressive(s.label)) : cap(w.running);
  const began = s.op?.startedAt ?? s.op?.createdAt;
  return { tone: "progress", words: began ? `${doing} · ${formatDuration(now - Date.parse(began))}` : doing };
}

/**
 * How far a running item has got, 0..1, by its steps and their usual times (as a task's bar), and whether some of it
 * can't be measured: no usual time yet, past it, or still starting. That part gets the sliding band.
 */
function itemFill(steps: TaskStep[] | null, now: number): { known: number; moving: boolean } {
  if (!steps) return { known: 0, moving: true };
  const p = progressOf({ steps }, now);
  return { known: Math.min(1, p.done + (p.current ?? 0)), moving: p.current === null || steps.some((s) => s.state === "starting") };
}

/**
 * A restore point's chain, a chip a step: "✓ Restore point › Update", the current one with a spinner. Its words are
 * the steps' names; the item's own chip says what's happening and for how long.
 */
function ItemSteps({ steps }: { steps: TaskStep[] }) {
  const update = steps[0]!.op?.followUp?.type === "update";
  return (
    <ol className="flex flex-wrap items-center gap-1.5" aria-label="Steps">
      {steps.map((s, k) => {
        const current = s.state === "running" || s.state === "starting";
        const words = k === 0 ? (s.op?.kind === "backup.create" ? "Restore point" : s.label) : update ? "Update" : s.label;
        return (
          <li key={k} className="flex items-center gap-1.5" aria-current={current ? "step" : undefined}>
            {k ? <IconChevronRight size={12} className="text-faint" aria-hidden="true" /> : null}
            <StatusPill {...STEP_TONE[s.state]}>
              {words}
              {s.state === "skipped" ? <span className="sr-only"> (didn't run)</span> : null}
            </StatusPill>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * One environment, server update or site in a run: its name, where it stands in words and its log, then what it
 * installs with the versions ("LiteSpeed Cache 7.9.1 → 7.9.2"), a restore point's chain as its steps, and what went
 * wrong when something did.
 */
function RunItem({ i, steps, w, org, now, onOpen }: { i: BulkItem; steps: TaskStep[] | null; w: RunWords; org: WorkOrg | null; now: number; onOpen: () => void }) {
  const here = useOrgPath();
  // A run asked from another org links into that org.
  const path = org ? (p: string) => `/o/${org.slug}/${p}` : here;
  const list = i.updates?.list ?? [];
  // What didn't install comes first, so "and 2 more" never hides it.
  const lines = list.length ? fromLines([...list].sort((a, b) => Number(!!b.status && b.status !== "updated") - Number(!!a.status && a.status !== "updated"))) : null;
  const installed = list.filter((u) => u.status === "updated").length;
  const updated = list.some((u) => u.status) && installed < list.length ? `Updated ${installed} of ${list.length}` : "Updated";
  // An older API's items have no versions: what it installs, by name (a server's item says it in its label).
  const what = !lines && i.kind !== "server" ? (i.updates?.what ?? null) : null;
  const state = itemState(i, steps, w, updated, now);
  const why = i.status === "failed" ? i.error : i.status === "skipped" ? i.reason : null;
  return (
    <li className="overflow-hidden rounded-[10px] text-[12.5px] shadow-[inset_0_0_0_1px_var(--hx-line)]">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 bg-well px-3 py-2">
        {/* The name wraps rather than losing the environment at its end. */}
        <span className={cx("min-w-[8rem] flex-1 leading-[18px] font-medium break-words", i.status === "skipped" ? "text-dim" : "text-fg")}>{i.label ?? i.siteName ?? "A site you can't see"}</span>
        {/* The chip and its Details stay together: on a narrow tray both go under the name. */}
        <span className="flex max-w-full shrink-0 items-center gap-2">
          <StatusPill tone={state.tone} icon={state.icon}>
            <span className="tabular-nums">{state.words}</span>
          </StatusPill>
          {i.operationId ? (
            <Link
              to={i.kind === "server" ? path(`servers/${i.serverId}?op=${i.operationId}`) : i.orgSlug ? `/o/${i.orgSlug}/sites/${i.siteId}?op=${i.operationId}` : path(`sites/${i.siteId}?op=${i.operationId}`)}
              onClick={onOpen}
              className="shrink-0 font-medium text-link hover:underline focus-visible:outline-2 focus-visible:outline-brand-500"
            >
              Details
            </Link>
          ) : null}
        </span>
      </div>
      {/* What it installs, a row each with its versions, then its steps, then what went wrong. */}
      {lines ? <UpdateVersions updates={lines} max={3} rows="px-3 py-1.5" className="divide-y divide-line-soft border-t border-line" /> : what ? <p className="truncate border-t border-line px-3 py-1.5 text-dim">{what}</p> : null}
      {steps && steps.length > 1 ? (
        <div className="border-t border-line px-3 py-2">
          <ItemSteps steps={steps} />
        </div>
      ) : null}
      {why ? <p className={cx("line-clamp-3 border-t border-line px-3 py-1.5", i.status === "failed" ? "text-bad" : "text-dim")}>{why}</p> : null}
    </li>
  );
}

/** The list's order: what's happening at the top, then what's waiting, then what's finished (problems first). */
const LIST_RANK: Record<BulkItem["status"], number> = { running: 0, will_run: 1, queued: 1, failed: 2, succeeded: 3, skipped: 4 };
/** The bar's order: it fills from the left, finished items first, then running ones, then the empty track of what waits. */
const BAR_RANK: Record<BulkItem["status"], number> = { succeeded: 0, failed: 1, running: 2, will_run: 3, queued: 3, skipped: 4 };

/**
 * A bulk run as one task: what it is, a bar with a segment per item, chips for where it stands ("2 updating",
 * "1 waiting", "1 updated", "1 didn't update"; once it ends, how it went), and the items themselves, running ones
 * first, each with its log (a client's site through the client's org), under "Show the sites" when there are more
 * than five. A run from the Updates page has a row per environment ("KN Beta · Live") or server update
 * ("test-1 · PHP 8.3"), with the updates' versions and each one's steps: its work is shown here and nowhere else in
 * the tray. A run asked from another org names it under the title.
 */
function BulkRow({ b, idx, typical, now, onOpen }: { b: BulkRun; idx: OpIndex; typical: (serverId: string) => Typical | undefined; now: number; onOpen: () => void }) {
  const org = orgOf(b);
  const c = runCounts(b);
  const done = runFinished(b);
  const w = RUN_WORDS[b.action] ?? OTHER_RUN;
  const rows = b.items.map((i) => ({ i, steps: i.status === "queued" || i.status === "skipped" ? null : itemSteps(i.operationId, i.status, idx, typical) }));
  const items = rows.filter((r) => r.i.status !== "skipped");
  // The API's words while it runs ("Backing up 12 sites", "Updating PHP 8.3 on mail-1"); once it ends, what it did.
  const title = done ? (b.finishedTitle ?? runTitle(b.action, titleCount(b, items.map((r) => r.i)), true)) : (b.title ?? runTitle(b.action, titleCount(b, items.map((r) => r.i)), false));
  const failed = done && c.failed > 0;
  const waiting = c.total - c.done - c.running;
  // What's true now, a chip each, only what isn't zero.
  const chips: { key: string; tone: Tone; icon?: PillIcon; words: string }[] = [];
  if (c.running) chips.push({ key: "running", tone: "progress", words: `${c.running} ${w.running}` });
  if (waiting) chips.push({ key: "waiting", tone: "neutral", icon: "clock", words: `${waiting} waiting` });
  if (c.succeeded) chips.push({ key: "done", tone: "good", words: `${c.succeeded} ${w.done}` });
  if (c.failed) chips.push({ key: "failed", tone: "bad", words: `${c.failed} ${w.failed}` });
  if (c.skipped) chips.push({ key: "skipped", tone: "neutral", icon: "dash", words: `${c.skipped} skipped` });
  // A long run's list: open from the start once one fails, unless the person opened or closed it.
  const [listOpen, setListOpen] = useState<boolean | null>(null);
  const listShown = listOpen ?? c.failed > 0;
  const list = (
    <ul className="mt-2.5 space-y-2">
      {[...rows]
        .sort((x, y) => LIST_RANK[x.i.status] - LIST_RANK[y.i.status])
        .map(({ i, steps }) => (
          <RunItem key={i.key} i={i} steps={steps} w={w} org={org} now={now} onOpen={onOpen} />
        ))}
    </ul>
  );
  return (
    <li className="px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <p className={cx("min-w-0 text-[14px] leading-5 font-medium", failed ? "text-bad" : "text-fg")}>
          {failed ? (
            <IconAlertCircle size={14} className="mr-1 mb-[2px] inline-block align-middle" aria-label="Finished with problems:" />
          ) : done ? (
            <IconCheckCircle size={14} className="mr-1 mb-[2px] inline-block align-middle text-good" aria-label="Finished:" />
          ) : (
            <Spinner size={13} className="mr-1.5 mb-[2px] inline-block align-middle text-info" aria-label="Running:" />
          )}
          {title}
        </p>
        <span className="shrink-0 pt-px text-[12px] leading-5 text-faint tabular-nums">{done ? relativeTime(b.finishedAt ?? b.createdAt, now) : formatDuration(now - Date.parse(b.createdAt))}</span>
      </div>
      {org ? <p className="truncate text-[12.5px] text-dim">{org.name}</p> : null}
      {/* A segment per item: finished ones full (green, or red when it failed), running ones as far as their usual time says, with a band sliding along what can't be measured, waiting ones an empty track. */}
      <div
        role="progressbar"
        aria-label={`${title}, progress`}
        aria-valuemin={0}
        aria-valuemax={c.total}
        aria-valuenow={c.done}
        aria-valuetext={chips.map((x) => x.words).join(", ")}
        className="mt-2 flex h-1.5 gap-0.5 overflow-hidden rounded-full"
      >
        {[...items]
          .sort((x, y) => BAR_RANK[x.i.status] - BAR_RANK[y.i.status])
          .map(({ i, steps }) => {
            const f = i.status === "running" ? itemFill(steps, now) : null;
            return (
              <span key={i.key} className="relative h-full flex-1 overflow-hidden bg-meter-track first:rounded-l-full last:rounded-r-full">
                {i.status === "succeeded" ? (
                  <span className="absolute inset-0 bg-good-fill" />
                ) : i.status === "failed" ? (
                  <span className="absolute inset-0 bg-bad-fill" />
                ) : f ? (
                  <>
                    <span className="absolute inset-y-0 left-0 bg-info-fill transition-[width] duration-700" style={{ width: `${f.known * 100}%` }} />
                    {f.moving ? <span className="hx-indeterminate absolute inset-y-0 right-0" style={{ left: `${f.known * 100}%` }} /> : null}
                  </>
                ) : null}
              </span>
            );
          })}
      </div>
      <ul className="mt-2 flex flex-wrap items-center gap-1.5" aria-label="Where it stands">
        {chips.map((x) => (
          <li key={x.key}>
            <StatusPill tone={x.tone} icon={x.icon}>
              {x.words}
            </StatusPill>
          </li>
        ))}
      </ul>
      {b.items.length <= 5 ? (
        list
      ) : (
        <details className="group mt-1.5" open={listShown} onToggle={(e) => setListOpen((e.currentTarget as HTMLDetailsElement).open)}>
          <summary className="w-fit cursor-pointer text-[12.5px] font-medium text-link select-none hover:underline">
            {listShown ? "Hide" : "Show"} the {itemsWords(b)}
          </summary>
          {list}
        </details>
      )}
    </li>
  );
}

/** "Installed 6 security updates. No restart needed." */
const outcomeWords = (o: NonNullable<Task["outcome"]>) => (o.detail ? `${o.title}. ${o.detail}` : o.title);

/** A finished task's answer: its tone's icon, then the words, up to two lines ("Installed 6 security updates. No restart needed."). */
function TaskLine({ tone, children }: { tone: "good" | "bad"; children: ReactNode }) {
  return (
    <p className="flex min-w-0 items-start gap-1.5 py-[3px] leading-[18px]">
      <ToneIcon tone={tone} size={14} className="mt-[2px]" />
      <span className={cx("line-clamp-2 min-w-0", tone === "bad" ? "font-medium text-bad" : "text-body")}>{children}</span>
    </p>
  );
}

function logHref(here: (p: string) => string, t: Task) {
  // Another org's work: its log in that org.
  const path = t.org ? (p: string) => `/o/${t.org!.slug}/${p}` : here;
  return t.siteId ? path(`sites/${t.siteId}?op=${t.logOp}`) : path(`servers/${t.serverId}?op=${t.logOp}`);
}

function TaskRow({ t, where, now, onOpen }: { t: Task; where: string | null; now: number; onOpen: () => void }) {
  const path = useOrgPath();
  const running = t.status === "running";
  const p = running ? progressOf(t, now) : null;
  const current = t.steps.find((s) => s.state === "running" || s.state === "starting");
  const failed = t.status === "failed";
  return (
    <li className="px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <p className={cx("min-w-0 text-[14px] leading-5 font-medium", failed ? "text-bad" : "text-fg")}>
          {failed ? (
            <IconAlertCircle size={14} className="mr-1 mb-[2px] inline-block align-middle" aria-label="Failed:" />
          ) : !running ? (
            <IconCheckCircle size={14} className="mr-1 mb-[2px] inline-block align-middle text-good" aria-label="Finished:" />
          ) : (
            <Spinner size={13} className="mr-1.5 mb-[2px] inline-block align-middle text-info" aria-label="Running:" />
          )}
          {t.title}
        </p>
        <span className="shrink-0 pt-px text-[12px] leading-5 text-faint tabular-nums">
          {running ? formatDuration(now - Date.parse(t.startedAt)) : t.finishedAt ? relativeTime(t.finishedAt, now) : ""}
        </span>
      </div>
      {where ? <p className="truncate text-[12.5px] text-dim">{where}</p> : null}
      <StepLine steps={t.steps} />
      {running ? <TaskBar t={t} now={now} /> : null}
      {/* Where it stands, a chip or a status line, with its Details on the same row. */}
      <div className="mt-2 flex items-start gap-3 text-[12.5px]">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          {running ? (
            current?.state === "starting" ? (
              <StatusPill tone="progress">Starting</StatusPill>
            ) : p?.slow ? (
              <StatusPill tone="warn">Taking longer than usual</StatusPill>
            ) : current?.estimateMs ? (
              <StatusPill tone="neutral" icon="clock">
                Usually about {formatDuration(current.estimateMs)}
              </StatusPill>
            ) : (
              <StatusPill tone="neutral" icon="info">
                First time timed here
              </StatusPill>
            )
          ) : t.status === "cancelled" ? (
            <StatusPill tone="neutral">Canceled</StatusPill>
          ) : (
            <TaskLine tone={failed || t.outcome?.tone === "bad" ? "bad" : "good"}>{failed ? (t.outcome ? outcomeWords(t.outcome) : (t.error ?? "It didn't finish.")) : t.outcome ? outcomeWords(t.outcome) : `Took ${formatDuration(t.finishedAt ? Date.parse(t.finishedAt) - Date.parse(t.startedAt) : null)}`}</TaskLine>
          )}
        </div>
        <Link to={logHref(path, t)} onClick={onOpen} className="shrink-0 leading-6 font-medium text-link hover:underline focus-visible:outline-2 focus-visible:outline-brand-500">
          Details
        </Link>
      </div>
    </li>
  );
}

/** The button beside the bell, and its panel. Hidden when nothing has run in the last ten minutes. */
export function TaskTray() {
  const org = useOrg();
  const qc = useQueryClient();
  const toast = useToast();
  const { running: opsRunning, recent: opsRecent, runs, idx, typical, moves, siteName, where, now } = useTasks();
  const runsGoing = runs.filter((b) => !runFinished(b));
  const runsDone = runs.filter(runFinished);
  // For the button and the headings: a bulk run counts as one task, and so does a server move.
  const running = [...runsGoing, ...moves.running, ...opsRunning];
  const recent = [...runsDone, ...moves.recent, ...opsRecent];

  // A bulk run that finishes while this tab is open says how it went, in whichever org it was asked from.
  const seen = useRef(new Map<string, boolean>());
  useEffect(() => {
    for (const b of runs) {
      const finished = runFinished(b);
      const before = seen.current.get(b.id);
      // Said, or seen finished: an older copy (the person's work elsewhere, read before it ended) never says it again.
      if (before === true) continue;
      // Seen running here, or started from this tab (it may have ended before the tray first read it).
      const mine = finished && takeAnnouncement(b.id);
      if (finished && (before === false || mine)) {
        const c = runCounts(b);
        const many = b.action === "updates.selected";
        // What it did (the API's words for the Updates page's runs: "Installed PHP 8.3.36 on mail-1"), and what failed.
        const head = b.finishedTitle ?? runTitle(b.action, titleCount(b, b.items.filter((i) => i.status === "succeeded")), true);
        toast({
          tone: c.failed ? "error" : "success",
          title: c.failed && (c.succeeded || !b.finishedTitle) ? `${head}; ${titleCount(b, b.items.filter((i) => i.status === "failed"))} failed` : head,
          description: c.failed ? (many ? "Each one's log says what happened." : "Each site's log says what happened.") : c.skipped ? `${plural(c.skipped, many ? "item" : "site")} skipped.` : undefined,
          action: { label: "See details", onClick: openTray },
        });
      }
      seen.current.set(b.id, finished);
    }
  }, [runs, toast]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const l = () => setOpen(true);
    openListeners.add(l);
    return () => {
      openListeners.delete(l);
    };
  }, []);

  // "Started · See progress" for work people start and wait on, wherever they started it: work in another org (a
  // client's site, from the agency's Sites list) is in the tray too, as the person's own (api/work.ts reads it again).
  useEffect(
    () =>
      onWatchStart((id) => {
        const home = watchedHomeOf(id);
        const orgId = home?.id ?? org.id;
        void qc
          .fetchQuery({ queryKey: keys.operation(orgId, id), queryFn: ({ signal }) => api.get<OperationWithLogs>(`/orgs/${orgId}/operations/${id}`, signal), staleTime: 2_000 })
          .then((res) => {
            const op = res.operation;
            if (!op.followUp && !START_TOAST_KINDS.has(op.kind)) return;
            const title = op.followUp ? `${op.followUp.label}, after ${isPhpFirst(op) || isCopyFirst(op) ? progressive(op.title).replace(/^./, (c) => c.toLowerCase()) : "a restore point"}` : op.title;
            toast({ tone: "info", title: `Started: ${title}`, action: { label: "See progress", onClick: openTray } });
            if (orgId === org.id) void qc.invalidateQueries({ queryKey: keys.operationLists(org.id) });
          })
          .catch(() => {
            /* the tray still shows it from the list */
          });
      }),
    [org.id, qc, toast],
  );

  if (!running.length && !recent.length) return null;
  // What's running, named when it's one kind: a run counts what it still has to do ("Installing 5 updates", 2 done: 3).
  const work = [...runsGoing.map((b) => ({ kind: b.action, count: Math.max(1, titleCount(b, b.items.filter(isActiveItem))) })), ...moves.running.map(() => ({ kind: "env.move", count: 1 })), ...opsRunning.map((t) => ({ kind: t.kind, count: 1 }))];
  const words = work.length ? runningWords(work) : "";
  const failedRecently = opsRecent.some((t) => t.status === "failed") || runsDone.some((b) => runCounts(b).failed > 0) || moves.recent.some((m) => m.state === "failed" || !!m.error);
  return (
    <P.Root open={open} onOpenChange={setOpen}>
      <P.Trigger asChild>
        <button
          type="button"
          aria-label={running.length ? `Running: ${words}` : `Done recently: ${recent.length}`}
          className="relative inline-flex h-9 items-center gap-1.5 rounded-[9px] px-2 text-[13px] font-medium text-body transition-colors hover:bg-hover hover:text-fg focus-visible:outline-2 focus-visible:outline-brand-500 data-[state=open]:bg-hover data-[state=open]:text-fg"
        >
          {running.length ? (
            <>
              <Spinner size={15} className="text-info" />
              {/* A phone has room for the count; the words are in the label. */}
              <span className="tabular-nums sm:hidden">{work.reduce((n, w) => n + w.count, 0)}</span>
              <span className="hidden sm:inline">{words} running</span>
            </>
          ) : failedRecently ? (
            <IconAlertCircle size={17} className="text-bad" />
          ) : (
            <IconCheckCircle size={17} className="text-good" />
          )}
        </button>
      </P.Trigger>
      <P.Portal>
        <P.Content align="end" sideOffset={8} collisionPadding={12} className="hx-pop z-[60] outline-none">
          <div className="flex max-h-[min(600px,calc(100dvh-88px))] w-[min(400px,calc(100vw-24px))] flex-col overflow-hidden rounded-[14px] bg-raised shadow-[var(--hx-shadow-pop)]">
            <div className="border-b border-line px-4 py-3">
              <h2 className="text-[15px] font-semibold text-fg">{running.length ? "Running" : "Done recently"}</h2>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {running.length ? (
                <ul className="divide-y divide-line-soft">
                  {runsGoing.map((b) => (
                    <BulkRow key={b.id} b={b} idx={idx} typical={typical} now={now} onOpen={() => setOpen(false)} />
                  ))}
                  {moves.running.map((m) => (
                    <MoveTaskRow key={m.id} m={m} siteName={siteName(m)} org={orgOf(m)} now={now} onOpen={() => setOpen(false)} />
                  ))}
                  {opsRunning.map((t) => (
                    <TaskRow key={t.id} t={t} where={where(t)} now={now} onOpen={() => setOpen(false)} />
                  ))}
                </ul>
              ) : null}
              {running.length && recent.length ? <h3 className="sticky top-0 border-t border-line-soft bg-raised px-4 pt-2.5 pb-1 text-[12px] font-medium text-dim">Done recently</h3> : null}
              {recent.length ? (
                <ul className="divide-y divide-line-soft">
                  {runsDone.map((b) => (
                    <BulkRow key={b.id} b={b} idx={idx} typical={typical} now={now} onOpen={() => setOpen(false)} />
                  ))}
                  {moves.recent.map((m) => (
                    <MoveTaskRow key={m.id} m={m} siteName={siteName(m)} org={orgOf(m)} now={now} onOpen={() => setOpen(false)} />
                  ))}
                  {opsRecent.map((t) => (
                    <TaskRow key={t.id} t={t} where={where(t)} now={now} onOpen={() => setOpen(false)} />
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        </P.Content>
      </P.Portal>
    </P.Root>
  );
}
